import React, { useEffect, useState, useSyncExternalStore } from "react";

import HttpManager from "../../../../services/HttpManager";
import { PbxDirectory } from "../../../../services/v2/prospects.service";
import { telefonoBonito } from "../../../../utils/telefonoBonito";
import { errorTexto } from "../salesHelpers";
import { telefono } from "./telefono";

/** El estado del teléfono para React (se vuelve a pintar cuando cambia). */
let version = 0;
telefono.suscribir(() => { version++; });
export const useTelefono = () => {
	useSyncExternalStore((cb) => telefono.suscribir(cb), () => version);
	return telefono;
};

const reloj = (desde: number | null): string => {
	if (!desde) return "";
	const s = Math.max(0, Math.round((Date.now() - desde) / 1000));
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const TECLAS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

/**
 * Botón del teléfono en la barra de Ventas: encender (pide el micrófono), estado y extensión.
 * Mientras la central no esté conectada a las líneas, el servidor dice enabled=false y aquí se explica.
 */
export const TelefonoBoton: React.FC<{ campusId: number }> = ({ campusId }) => {
	const tel = useTelefono();
	const [aviso, setAviso] = useState<string | null>(null);
	const encender = async () => {
		setAviso(null);
		try {
			const r = await HttpManager.getInstance().prospectsService.pbx(campusId);
			if (!r.data.enabled) { setAviso("El teléfono de WAPP todavía no está conectado a las líneas."); return; }
			await tel.conectar(r.data);
		} catch (e) { setAviso(errorTexto(e)); }
	};
	const [abierto, setAbierto] = useState<boolean>(false);
	const [numero, setNumero] = useState<string>("");
	const marcar = () => {
		const n = numero.replace(/[^\d*#]/g, "");
		if (!n) return;
		// 8 dígitos = Honduras, 10 = México; con lada o extensión se marca tal cual.
		const d = n.length === 8 ? "504" + n : n.length === 10 ? "52" + n : n;
		if (tel.llamar(d, {})) { setNumero(""); setAbierto(false); }
	};
	const txt = tel.estado === "listo" ? `Teléfono · ext ${tel.creds?.extension}` : tel.estado === "conectando" ? "Conectando…" : tel.estado === "error" ? "Teléfono: reintentar" : "Encender teléfono";
	return (
		<span className="tel-boton-caja">
			<button className={"btn-wa tel-boton " + tel.estado} aria-expanded={tel.estado === "listo" ? abierto : undefined}
				onClick={tel.estado === "listo" ? () => setAbierto(!abierto) : encender}
				title={tel.estado === "listo" ? "Marcar un número o apagar el teléfono" : tel.error || "Usa los audífonos de la computadora"}>
				<span className="tel-punto" aria-hidden="true" />{txt}
			</button>
			{aviso || (tel.estado === "error" && tel.error) ? <span className="tel-aviso">{aviso || tel.error}</span> : null}
			{abierto && tel.estado === "listo" ? (
				<span className="tel-menu">
					<label htmlFor="tel-num">Marcar número o extensión</label>
					<span className="tel-menu-fila">
						<input id="tel-num" type="tel" value={numero} autoFocus placeholder="9958 2425 · 81 3547 0734 · 301"
							onChange={(e) => setNumero(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") marcar(); if (e.key === "Escape") setAbierto(false); }} />
						<button className="btn-wa pri" disabled={!numero.trim() || tel.ocupado} onClick={marcar}>Llamar</button>
					</span>
					<button className="enlace" onClick={() => { tel.apagar(); setAbierto(false); }}>Apagar teléfono</button>
				</span>
			) : null}
		</span>
	);
};

/**
 * Panel de la llamada en curso (abajo a la derecha, encima de todo): quién, cuánto lleva, silenciar, espera, teclado,
 * transferir (directa o con aviso) y colgar. Para la entrante: contestar, rechazar y abrir su ficha.
 */
export const TelefonoPanel: React.FC<{ campusId: number; onAbrir: (prospectId: number) => void }> = ({ campusId, onAbrir }) => {
	const tel = useTelefono();
	const l = tel.llamada;
	const [, tick] = useState(0);
	const [modo, setModo] = useState<"teclado" | "transferir" | null>(null);
	const [dir, setDir] = useState<PbxDirectory | null>(null);
	const [destino, setDestino] = useState<string>("");
	const [buscando, setBuscando] = useState<boolean>(false);

	useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
	useEffect(() => { if (!l) { setModo(null); setDestino(""); } }, [l]);
	useEffect(() => {
		if (modo === "transferir" && !dir) HttpManager.getInstance().prospectsService.pbxDirectory(campusId).then((r) => setDir(r.data)).catch(() => setDir({ extensions: [], teams: [] }));
	}, [modo]);

	// Entrante: buscar a quién es el número para abrir su ficha.
	const abrirFicha = async () => {
		if (!l) return;
		if (l.prospectId) { onAbrir(l.prospectId); return; }
		setBuscando(true);
		try {
			const r = await HttpManager.getInstance().prospectsService.lookupPhone(campusId, "+" + l.number.replace(/\D/g, ""));
			if (r.data.match?.id) { tel.ligar(r.data.match.id, [r.data.match.first_name, r.data.match.last_name].filter(Boolean).join(" ")); onAbrir(r.data.match.id); }
		} catch (e) { /* sin ficha: se puede dar de alta desde Nuevo prospecto */ }
		finally { setBuscando(false); }
	};

	if (!l) return null;
	const interna = l.number.length <= 4;
	const quien = l.name || (interna ? "Extensión " + l.number : telefonoBonito("+" + l.number));
	const estadoTxt = l.status === "entrante" ? "Llamada entrante" : l.status === "marcando" ? "Marcando…" : l.status === "sonando" ? "Sonando…"
		: l.status === "terminada" ? "Terminó" : (l.held ? "En espera · " : "") + reloj(l.startedAt);

	return (
		<div className={"tel-panel " + l.status} role="dialog" aria-label="Llamada">
			<div className="tel-cab">
				<div>
					<div className="tel-quien">{quien}</div>
					<div className="tel-estado">{l.direction === "in" ? "Entrante · " : ""}{estadoTxt}{l.muted ? " · micrófono apagado" : ""}</div>
				</div>
				{!interna ? <button className="enlace" disabled={buscando} onClick={abrirFicha}>Ficha</button> : null}
			</div>

			{l.status === "entrante" ? (
				<div className="tel-acc">
					<button className="btn-wa pri" onClick={() => { tel.contestar(); abrirFicha(); }}>Contestar</button>
					<button className="btn-wa tel-colgar" onClick={() => tel.colgar()}>Rechazar</button>
				</div>
			) : l.status !== "terminada" ? (
				<>
					{l.consult ? (
						<div className="tel-consulta">
							<span>Con {l.consult.number}: {l.consult.status === "en_llamada" ? "hablando (el cliente espera)" : "marcando…"}</span>
							<div className="tel-acc">
								<button className="btn-wa pri" disabled={l.consult.status !== "en_llamada"} onClick={() => tel.completarTransferencia()}>Pasarle la llamada</button>
								<button className="btn-wa" onClick={() => tel.cancelarConsulta()}>Regresar con el cliente</button>
							</div>
						</div>
					) : null}
					<div className="tel-acc">
						<button className={"btn-wa" + (l.muted ? " on" : "")} aria-pressed={l.muted} onClick={() => tel.silenciar()}>{l.muted ? "Activar micro" : "Silenciar"}</button>
						<button className={"btn-wa" + (l.held ? " on" : "")} aria-pressed={l.held} disabled={!l.startedAt} onClick={() => tel.espera()}>{l.held ? "Quitar espera" : "Espera"}</button>
						<button className={"btn-wa" + (modo === "teclado" ? " on" : "")} onClick={() => setModo(modo === "teclado" ? null : "teclado")}>Teclado</button>
						<button className={"btn-wa" + (modo === "transferir" ? " on" : "")} disabled={!l.startedAt || !!l.consult} onClick={() => setModo(modo === "transferir" ? null : "transferir")}>Transferir</button>
						<button className="btn-wa tel-colgar" onClick={() => tel.colgar()}>Colgar</button>
					</div>
					{modo === "teclado" ? (
						<div className="tel-teclado">{TECLAS.map((k) => <button key={k} className="btn-wa" onClick={() => tel.tecla(k)}>{k}</button>)}</div>
					) : null}
					{modo === "transferir" ? (
						<div className="tel-transferir">
							<select value={destino} onChange={(e) => setDestino(e.target.value)} aria-label="Transferir a">
								<option value="">{dir ? "¿A quién?" : "Cargando…"}</option>
								{dir?.teams.length ? <optgroup label="Equipos (suena en todas)">{dir.teams.map((t) => <option key={t.extension} value={t.extension}>{t.team} · {t.extension}</option>)}</optgroup> : null}
								{dir ? <optgroup label="Personas">{dir.extensions.filter((x) => x.extension !== tel.creds?.extension).map((x) => <option key={x.extension} value={x.extension}>{x.name} · {x.extension}</option>)}</optgroup> : null}
							</select>
							<div className="tel-acc">
								<button className="btn-wa" disabled={!destino} onClick={() => { tel.consultar(destino); setModo(null); }}>Con aviso</button>
								<button className="btn-wa pri" disabled={!destino} onClick={() => { tel.transferir(destino); setModo(null); }}>Directa</button>
							</div>
						</div>
					) : null}
				</>
			) : null}
		</div>
	);
};
